import { supabase } from '@/lib/supabase';
import { Invoice, ApparelOrder } from '@/types/models';

export const getInvoices = async (organizationId: string): Promise<Invoice[]> => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        family:family_id (*)
      `)
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as Invoice[];
  } catch (error) {
    console.error('Error fetching invoices:', error);
    return [];
  }
};

export const getInvoiceById = async (invoiceId: string): Promise<Invoice | null> => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        family:family_id (*)
      `)
      .eq('id', invoiceId)
      .single();

    if (error) throw error;
    return data as Invoice;
  } catch (error) {
    console.error('Error fetching invoice:', error);
    return null;
  }
};

export const getInvoicesByFamily = async (familyId: string): Promise<Invoice[]> => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*')
      .eq('family_id', familyId)
      .order('due_date', { ascending: false });

    if (error) throw error;
    return data as Invoice[];
  } catch (error) {
    console.error('Error fetching family invoices:', error);
    return [];
  }
};

export const createInvoice = async (
  invoice: Pick<Invoice, 'family_id' | 'organization_id' | 'total_cents' | 'due_date' | 'status'>
): Promise<Invoice | null> => {
  try {
    console.log('InvoiceService.createInvoice called with:', invoice);
    
    // Generate invoice number for this organization
    const { data: invoiceNumber, error: numberError } = await supabase
      .rpc('generate_invoice_number', {
        org_id: invoice.organization_id
      });
    
    if (numberError) {
      console.error('Error generating invoice number:', numberError);
      throw numberError;
    }
    
    // Create invoice with generated number
    const invoiceWithNumber = {
      ...invoice,
      invoice_number: invoiceNumber
    };
    
    const { data, error } = await supabase
      .from('invoices')
      .insert(invoiceWithNumber)
      .select()
      .single();

    if (error) {
      console.error('Supabase error creating invoice:', error);
      throw error;
    }
    
    console.log('Invoice created successfully with number:', invoiceNumber);
    return data as Invoice;
  } catch (error) {
    console.error('Error creating invoice:', error);
    // Log the specific error details
    if (error instanceof Error) {
      console.error('Error message:', error.message);
    }
    return null;
  }
};

export const updateInvoice = async (
  invoiceId: string,
  updates: Partial<Invoice>
): Promise<Invoice | null> => {
  try {
    const { data, error } = await supabase
      .from('invoices')
      .update(updates)
      .eq('id', invoiceId)
      .select()
      .single();

    if (error) throw error;
    return data as Invoice;
  } catch (error) {
    console.error('Error updating invoice:', error);
    return null;
  }
};

export const deleteInvoice = async (invoiceId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('invoices')
      .delete()
      .eq('id', invoiceId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting invoice:', error);
    return false;
  }
};

export const createInvoiceLineItem = async (
  lineItem: {
    invoice_id: string;
    description: string;
    amount_cents: number;
    class_id?: string;
    student_id?: string;
  }
): Promise<any> => {
  try {
    const { data, error } = await supabase
      .from('invoice_line_items')
      .insert(lineItem)
      .select()
      .single();
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating invoice line item:', error);
    return null;
  }
};

export const getInvoiceLineItems = async (invoiceId: string): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('invoice_line_items')
      .select(`
        *,
        class:class_id (id, name),
        student:student_id (id, first_name, last_name)
      `)
      .eq('invoice_id', invoiceId);
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching invoice line items:', error);
    return [];
  }
};

// Enhanced customer management helper (internal, elegant)
const ensureStripeCustomerExists = async (
  familyId: string, 
  organizationId: string, 
  customerEmail: string
): Promise<string | null> => {
  try {
    // Check if customer already exists
    const { data: organization } = await supabase
      .from('organizations')
      .select('billing_settings')
      .eq('id', organizationId)
      .single();

    const stripeAccountId = organization?.billing_settings?.stripe_account_id || null;

    const { data: existingCustomer } = await supabase
      .from('stripe_customers')
      .select('stripe_customer_id')
      .eq('family_id', familyId)
      .eq('organization_id', organizationId)
      .eq('stripe_account_id', stripeAccountId || 'platform')
      .single();

    if (existingCustomer) {
      return existingCustomer.stripe_customer_id;
    }

    // Create customer elegantly in background
    const { data: newCustomer } = await supabase.functions.invoke('create-family-stripe-customer', {
      body: {
        familyId,
        organizationId,
        customerEmail
      }
    });

    return newCustomer?.customer_id || null;
  } catch (error) {
    console.warn('Customer creation failed, proceeding with payment anyway:', error);
    return null; // Graceful degradation - payment can still work without customer record
  }
};

export const createInvoicePaymentSession = async ({
  invoiceId,
  familyId,
  organizationId,
  amount_cents,
  amountCents,
  amount,
  customer_email,
  customerEmail,
  success_url,
  cancel_url,
  successUrl,
  cancelUrl
}: {
  invoiceId: string;
  familyId: string;
  organizationId: string;
  amount_cents?: number;
  amountCents?: number;
  amount?: number;
  customer_email?: string;
  customerEmail?: string;
  success_url?: string;
  cancel_url?: string;
  successUrl?: string;
  cancelUrl?: string;
}): Promise<{ url: string } | null> => {
  try {
    const email = customer_email ?? customerEmail;
    
    // Elegantly ensure customer exists (invisible to caller)
    if (email && familyId && organizationId) {
      await ensureStripeCustomerExists(familyId, organizationId, email);
    }

    // Continue with existing payment flow (unchanged interface)
    const { data, error } = await supabase.functions.invoke('create-one-time-payment', {
      body: {
        amount_cents: amount_cents ?? amountCents ?? (amount ? Math.round(amount * 100) : 0),
        currency: 'usd',
        name: `Invoice Payment: ${invoiceId}`,
        description: `Payment for invoice ${invoiceId}`,
        customer_email: email,
        success_url: success_url || successUrl,
        cancel_url: cancel_url || cancelUrl,
        metadata: {
          family_id: familyId,
          organization_id: organizationId,
          invoice_id: invoiceId
        }
      }
    });
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating invoice payment session:', error);
    return null;
  }
};

export const getPaymentsByFamily = async (familyId: string): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('payments')
      .select(`*, invoice:invoice_id (id, total_cents, due_date)`)
      .eq('family_id', familyId)
      .order('paid_at', { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching payments:', error);
    return [];
  }
};

export const searchInvoicesByNumber = async (
  organizationId: string,
  searchTerm: string
): Promise<Invoice[]> => {
  try {
    // If search term is empty, return empty array
    if (!searchTerm.trim()) {
      return [];
    }

    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        family:family_id (*)
      `)
      .eq('organization_id', organizationId)
      .ilike('invoice_number', `%${searchTerm}%`)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) throw error;
    return data as Invoice[];
  } catch (error) {
    console.error('Error searching invoices by number:', error);
    return [];
  }
};

export const InvoiceService = {
  getInvoices,
  getInvoiceById,
  getInvoicesByFamily,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  createInvoiceLineItem,
  getInvoiceLineItems,
  createInvoicePaymentSession,
  getPaymentsByFamily,
  searchInvoicesByNumber
};
